import { useCallback, useEffect, useMemo } from "react";
import { useRouter } from "next/router";
import { msg, t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { listingStore } from "src/stores/listing/listing.store";
import { useListingState } from "src/hooks/stores/use-listing-store";
import type { ListingSortKeys } from "src/types/adapters/listing.type";
import { SortDropdown } from "./Sort.styled";
import type { MessageDescriptor } from "@lingui/core";
import { isListingSortKey } from "src/utils/type-guards";
import {
  MAP_SORT_TO_URL_VALUE,
  MAP_URL_VALUE_TO_SORT,
  ORDER_BY_PARAM,
} from "src/utils/search-provider/constants";

type SortProps = {
  handleScrollToTop: () => void;
};

const SORT_TRANSLATION = {
  new: msg`New`,
  "new-discount": msg`New discount`,
  "price-high-to-low": msg`Price: high to low`,
  "price-low-to-high": msg`Price: low to high`,
  popularity: msg`Popularity`,
} as const satisfies Record<ListingSortKeys, MessageDescriptor>;

function Sort({ handleScrollToTop }: SortProps): JSX.Element {
  const { i18n, _ } = useLingui();
  const router = useRouter();
  const { sort, sortOptions } = useListingState();

  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    const orderByParam =
      typeof router.query[ORDER_BY_PARAM] === "string"
        ? router.query[ORDER_BY_PARAM]
        : "";
    const { sort: currentSort } = listingStore.getSnapshot();
    const sortKey = MAP_URL_VALUE_TO_SORT[orderByParam];

    if (isListingSortKey(sortKey)) {
      if (sortKey !== currentSort) {
        listingStore.setSort(sortKey, sortOptions[sortKey]);
      }
    } else if (!orderByParam && currentSort) {
      listingStore.setSort("", undefined);
    }
  }, [router.isReady, router.query, sortOptions]);

  const dropdownOptions = useMemo(
    () =>
      sortOptions
        ? Object.entries(sortOptions).map(([key]) => ({
            value: key,
            text: _(SORT_TRANSLATION[key as ListingSortKeys]),
          }))
        : [],
    [sortOptions, _]
  );

  const displayText = useMemo(
    () =>
      sort && isListingSortKey(sort)
        ? _(SORT_TRANSLATION[sort])
        : t(i18n)`Sort`,
    [_, i18n, sort]
  );

  const handleChange = useCallback(
    (_event: MouseEvent, { value }: { value: string }) => {
      const nextSort = isListingSortKey(value) ? value : "";
      const nextOption = nextSort ? sortOptions[nextSort] : undefined;

      listingStore.setSort(nextSort, nextOption);

      const url = new URL(location.toString());

      if (nextSort) {
        url.searchParams.set(ORDER_BY_PARAM, MAP_SORT_TO_URL_VALUE[nextSort]);
      } else {
        url.searchParams.delete(ORDER_BY_PARAM);
      }

      url.searchParams.delete("page");

      void router.push(url.toString(), undefined, {
        scroll: false,
        shallow: true,
      });

      handleScrollToTop();
    },
    [router, sortOptions, handleScrollToTop]
  );

  return (
    <SortDropdown
      data-testid="product-sorting-select"
      value={sort}
      text={displayText}
      options={dropdownOptions}
      onChange={handleChange}
    />
  );
}

export { Sort };
